import type { AcpPlanData } from '@crowdex/agent-contracts';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import { type ComponentProps, useCallback, useEffect, useMemo } from 'react';
import type { AgentTerminalDisplay } from '@/components/desktop-panel-primitives';
import { SidePanel } from '@/components/desktop-side-panel';
import { browserElementSelection } from '@/lib/browser-context';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import type { CloudConversationStartupPhase } from '@/lib/conversations/cloud-conversation-startup';
import { PREVIEW_PULL_REQUEST_COCKPIT, PREVIEW_REVIEW_DIFF } from '@/lib/desktop-preview-data';
import { appTargetsWithTabs, maximizePanel, restorePanel } from '@/lib/desktop-shell/app-panels';
import { whileConversationShown } from '@/lib/desktop-shell/conversation-scope';
import { parkedPanelTabIds } from '@/lib/desktop-shell/panel-layouts';
import {
  panelSplitWithTabs,
  splitPanelTab,
  unsplitPanelTab,
} from '@/lib/desktop-shell/panel-split';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { formatElementSelection } from '@/lib/element-selection';
import type { NativeAgent } from '@/lib/native-desktop';
import { readNativeGitStatus } from '@/lib/native-desktop';
import { pullRequestForBranch } from '@/lib/pull-request';
import { mentionTargetsEqual } from '@/lib/side-panel/mention-targets';
import { showPanelView } from '@/lib/side-panel/panel-tabs';

type SidePanelProps = ComponentProps<typeof SidePanel>;

/**
 * The right-hand side panel (summary / plan / review / terminal / files /
 * browser / subagent tabs) plus its embedded conversation summary. All of the
 * panel's own UI state (which view, size, review diff, pending browser preview,
 * terminal action) lives in the store and is read directly here; the shell only
 * injects the conversation/connection-derived data and the business callbacks
 * (send message, approve plan, git text generation, workspace actions) it owns.
 * Store-only interactions (open/close, resize, view selection, browser
 * selection capture, mention targets, git-status refresh) are handled inline so
 * they no longer clutter the shell.
 */
export function ConversationSidePanel({
  agent,
  pluginPermissions,
  cloudRuntime,
  cloudAgentSelected,
  cloudProvisionError,
  cloudConversationStartupPhase,
  discoveringAgents,
  preview,
  pluginConversation,
  displayedPlan,
  selectedSubagent,
  visibleAgentTerminals,
  cloudPullRequest,
  onAnnotateSelection,
  onAskSelectionInSideChat,
  onSubmitReview,
  onKillAgentTerminal,
  onAgentTerminalStopped,
  resolveReference,
  generateGitText,
  presentation = 'docked',
}: {
  /** The session model's structured text, for a pull request's title and description. */
  generateGitText?: SidePanelProps['generateGitText'];
  /** `screen`: a screen of the phone app (see `SidePanel`), sized by it. */
  presentation?: 'docked' | 'screen';
  agent: NativeAgent | null;
  pluginPermissions: ReadonlySet<string>;
  cloudRuntime: CloudRuntime | null;
  cloudAgentSelected: boolean;
  cloudProvisionError: string | null;
  cloudConversationStartupPhase: CloudConversationStartupPhase | null;
  discoveringAgents: boolean;
  preview: boolean;
  pluginConversation: SidePanelProps['pluginConversation'];
  displayedPlan: AcpPlanData | null;
  selectedSubagent: SidePanelProps['selectedSubagent'];
  visibleAgentTerminals: readonly AgentTerminalDisplay[];
  cloudPullRequest: PullRequestSummary | null;
  onAnnotateSelection: SidePanelProps['onAnnotateSelection'];
  onAskSelectionInSideChat: (content: string, autoSubmit?: boolean) => void;
  onSubmitReview: (review: string) => void;
  onKillAgentTerminal: (terminalId: string, outputFile?: string) => Promise<void>;
  onAgentTerminalStopped: (terminalId: string) => void;
  resolveReference: SidePanelProps['resolveReference'];
}) {
  const store = useDesktopShellStoreApi();
  const chatId = useDesktopShellStore((state) => state.chatId);
  const workspace = useDesktopShellStore((state) => state.workspace);
  const sidePanelOpen = useDesktopShellStore((state) => state.sidePanelOpen);
  const panelTabs = useDesktopShellStore((state) => state.panelTabs);
  const panelLayouts = useDesktopShellStore((state) => state.panelLayouts);
  const retainedTabIds = useMemo(() => parkedPanelTabIds(panelLayouts), [panelLayouts]);
  const panelSplit = useDesktopShellStore((state) => state.panelSplit);
  // A tab closed leaves the split, and forgets what it showed (an app's item).
  useEffect(() => {
    const next = panelSplitWithTabs(panelSplit, panelTabs);
    if (next !== panelSplit) store.getState().setPanelSplit(next);
    store.getState().setAppPanelTargets((targets) => appTargetsWithTabs(targets, panelTabs));
  }, [panelSplit, panelTabs, store]);
  const panelSize = useDesktopShellStore((state) => state.panelSize);
  const gitStatus = useDesktopShellStore((state) => state.gitStatus);
  const syncedPullRequest = useDesktopShellStore((state) => state.syncedPullRequests[chatId]);
  const workspacePullRequest = useDesktopShellStore((state) =>
    workspace === null ? null : state.workspacePullRequests[workspace],
  );
  const messageReviewDiff = useDesktopShellStore((state) => state.messageReviewDiff);
  const terminalAction = useDesktopShellStore((state) => state.terminalAction);
  const agentTerminalRequestId = useDesktopShellStore((state) => state.agentTerminalRequestId);
  const sideChatPrompt = useDesktopShellStore((state) => state.sideChatPrompt);
  const localPullRequest = pullRequestForBranch(
    gitStatus?.pullRequest ?? null,
    syncedPullRequest ?? workspacePullRequest,
    gitStatus?.branch ?? null,
  );
  const setElementSelections = useDesktopShellStore((state) => state.setElementSelections);
  const setSidePanelOpen = useDesktopShellStore((state) => state.setSidePanelOpen);
  const setPanelTabs = useDesktopShellStore((state) => state.setPanelTabs);
  const setPanelSize = useDesktopShellStore((state) => state.setPanelSize);
  const setMessageReviewDiff = useDesktopShellStore((state) => state.setMessageReviewDiff);
  const setTerminalAction = useDesktopShellStore((state) => state.setTerminalAction);
  const setAgentTerminalRequestId = useDesktopShellStore(
    (state) => state.setAgentTerminalRequestId,
  );
  const setPanelMentionTargets = useDesktopShellStore((state) => state.setPanelMentionTargets);
  const publishPanelMentionTargets = useCallback(
    (targets: Parameters<SidePanelProps['onMentionTargetsChange']>[0]) => {
      setPanelMentionTargets((current) =>
        mentionTargetsEqual(current, targets) ? current : targets,
      );
    },
    [setPanelMentionTargets],
  );

  return (
    <SidePanel
      generateGitText={generateGitText}
      tabs={panelTabs}
      onTabsChange={setPanelTabs}
      retainedTabIds={retainedTabIds}
      split={panelSplit}
      onSplitTab={(tabId, targetId, side) => splitPanelTab(store, tabId, targetId, side)}
      onUnsplitTab={(tabId) => unsplitPanelTab(store, tabId)}
      agent={agent}
      pluginPermissions={pluginPermissions}
      cloudRuntime={cloudRuntime}
      cloudWorkspaceError={cloudAgentSelected ? cloudProvisionError : null}
      cloudWorkspacePhase={cloudConversationStartupPhase}
      cloudWorkspaceStarting={cloudAgentSelected && cloudRuntime === null && discoveringAgents}
      pluginConversation={pluginConversation}
      hidden={!sidePanelOpen}
      manualResize={presentation === 'docked'}
      presentation={presentation}
      onBrowserSelection={(selection) => {
        const context = formatElementSelection(
          browserElementSelection(selection),
          selection.annotationIndex,
        );
        setElementSelections((current) => [
          ...current,
          {
            context,
            ...(selection.screenshotDataUrl === undefined
              ? {}
              : { dataUrl: selection.screenshotDataUrl }),
            id: crypto.randomUUID(),
            title: selection.title,
            kind: 'web',
          },
        ]);
      }}
      onClose={() => setSidePanelOpen(false)}
      onMentionTargetsChange={publishPanelMentionTargets}
      onAnnotateSelection={onAnnotateSelection}
      onAskSelectionInSideChat={onAskSelectionInSideChat}
      onMoreDetailsSelection={(content) => onAskSelectionInSideChat(content, true)}
      onSubmitReview={(review) => {
        onSubmitReview(review);
        setSidePanelOpen(false);
      }}
      onPullRequestChange={async () => {
        // A refresh after an action: the git status sync reports a folder that cannot be read.
        if (workspace !== null && !preview) {
          const status = await readNativeGitStatus(workspace).catch(() => undefined);
          // Read for the conversation it was asked in, never shown in the next one.
          if (status !== undefined)
            whileConversationShown(store, chatId, (state) => state.setGitStatus(status));
        }
      }}
      onTerminalActionConsumed={() => setTerminalAction(null)}
      onSizeChange={(size) => setPanelSize(size)}
      maximized={panelSize >= 100}
      onToggleMaximize={() => (panelSize >= 100 ? restorePanel(store) : maximizePanel(store))}
      onSelectView={(view) => {
        if (view === 'review') setMessageReviewDiff(null);
        setPanelTabs((tabs) => showPanelView(tabs, view));
      }}
      onReveal={() => setSidePanelOpen(true)}
      plan={displayedPlan}
      pullRequest={cloudAgentSelected ? cloudPullRequest : localPullRequest}
      currentBranch={gitStatus?.branch ?? null}
      pullRequestCockpitOverride={preview ? PREVIEW_PULL_REQUEST_COCKPIT : undefined}
      reviewDiffOverride={messageReviewDiff ?? (preview ? PREVIEW_REVIEW_DIFF : null)}
      selectedSubagent={selectedSubagent}
      agentTerminals={visibleAgentTerminals}
      selectedAgentTerminalId={agentTerminalRequestId}
      onAgentTerminalRequestConsumed={() => setAgentTerminalRequestId(null)}
      onKillAgentTerminal={onKillAgentTerminal}
      onAgentTerminalStopped={onAgentTerminalStopped}
      sideChatPrompt={sideChatPrompt}
      resolveReference={resolveReference}
      terminalAction={terminalAction}
      taskId={chatId}
      size={panelSize}
      workspace={workspace}
    />
  );
}
